import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { ActivityStep } from "./PatientCard";
import { formatActivityTimestamp } from "@/lib/dataTransformers";
import { StaffInfoDisplay } from "./StaffInfoDisplay";
import { useState, useEffect } from "react";
import statusSkippedIcon from "@/assets/status-skipped.svg";
interface ParallelActivityGroupProps {
  parallelActivities: ActivityStep[];
  completedCount: number;
  totalCount: number;
}

// Helper function to format duration
const formatDuration = (minutes: number | undefined): string => {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
};

// Helper function to calculate elapsed time from assigned_at (prioritized) or started_at timestamp
const calculateElapsedTime = (assignedAt: string | undefined, startedAt: string | undefined): string => {
  const startTime = assignedAt || startedAt; // Prioritize assigned_at
  if (!startTime) return '00:00:00';
  
  const startDate = new Date(startTime);
  const now = new Date();
  
  const diffMs = now.getTime() - startDate.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Helper function to get color based on elapsed time
const getElapsedTimeColor = (assignedAt: string | undefined, startedAt: string | undefined): string => {
  const startTime = assignedAt || startedAt; // Prioritize assigned_at
  if (!startTime) return 'bg-muted/50 text-muted-foreground';
  
  const diffMs = Date.now() - new Date(startTime).getTime();
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (totalMinutes <= 30) return 'bg-success/10 text-success';
  if (totalMinutes <= 60) return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
};

export const ParallelActivityGroup = ({ parallelActivities }: ParallelActivityGroupProps) => {
  // Debug log to verify activity order
  console.log('🎨 ParallelActivityGroup received:', parallelActivities.map(a => ({
    name: a.name,
    workflowStep: a.workflowStep
  })));
  
  // Split activities into left and right columns
  const leftActivities = parallelActivities.slice(0, 2);
  const rightActivities = parallelActivities.slice(2, 4);
  
  console.log('🎨 Split into columns:', {
    left: leftActivities.map(a => a.name),
    right: rightActivities.map(a => a.name)
  });

  // State for real-time elapsed time updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update elapsed time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Calculate height for main vertical line
  const rowCount = Math.max(leftActivities.length, rightActivities.length);
  return (
    <div className="relative">
      {/* Main connecting line before parallel section */}

      {/* Parallel activities section */}
      <div className="relative pl-6 pt-4 pb-6">
        {/* Main vertical line running through parallel section on the left */}
        <div
          className={cn(
            "absolute left-[-23px] top-0 w-0.5 h-full",
            parallelActivities.every((a) => a.status === "completed")
              ? "bg-success"
              : "bg-border",
          )}
        />

        {/* Horizontal extension at the top with sharp corner */}

        {/* Two-column grid for parallel activities */}
        <div className="grid grid-cols-2 gap-x-12 gap-y-6">
          {/* Row 1 - Left: Discharge Summary Saved */}
          {leftActivities[0] && (
            <div className="relative flex gap-3">
              {/* Horizontal connector from main line */}

              {/* Vertical connector from horizontal line to circle */}

              {/* Status indicator */}
              <div className="relative flex-shrink-0 mt-0.5 z-10">
                {leftActivities[0].status === "completed" ? (
                  <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
                  </div>
                ) : leftActivities[0].status === "skipped" ? (
                  <div className="w-5 h-5 rounded-full bg-muted-foreground flex items-center justify-center">
                    <img src={statusSkippedIcon} alt="Skipped" className="w-3 h-3 invert" />
                  </div>
                ) : leftActivities[0].status === "in-progress" ? (
                  <div className="w-5 h-5 rounded-full border-2 border-success bg-background flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-success" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
                )}
              </div>

              {/* Activity content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-foreground">{leftActivities[0].name}</h4>

                    {leftActivities[0].description && (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {leftActivities[0].description}
                      </p>
                    )}

                    {leftActivities[0].status === "completed" && leftActivities[0].completedBy && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={leftActivities[0].completedBy} label="Completed by" size="sm" />
                      </div>
                    )}

                    {leftActivities[0].status === "in-progress" && leftActivities[0].assignedTo && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={leftActivities[0].assignedTo} label="Assigned to" size="sm" />
                      </div>
                    )}

                    {!leftActivities[0].completedBy &&
                      !leftActivities[0].assignedTo &&
                      (leftActivities[0].staffName || leftActivities[0].doctor) && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                          <span>{leftActivities[0].staffName || leftActivities[0].doctor}</span>
                        </div>
                      )}

                    {leftActivities[0].notes && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-1 rounded">
                        <span className="font-medium">Notes:</span> {leftActivities[0].notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0 gap-1">
                    {leftActivities[0].status === 'completed' && (
                      <>
                        <div className="text-xs text-muted-foreground">Completed:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[0].completionTime || leftActivities[0].timestamp)}
                        </span>
                        {leftActivities[0].timeTakenMinutes !== undefined && (
                          <>
                            <div className="text-xs text-muted-foreground mt-2">Duration:</div>
                            <span className="bg-success/10 text-success px-1.5 py-0.5 rounded text-xs font-medium">
                              {formatDuration(leftActivities[0].timeTakenMinutes)}
                            </span>
                          </>
                        )}
                      </>
                    )}

                    {leftActivities[0].status === 'in-progress' && (
                      <>
                        <div className="text-xs text-muted-foreground">Assigned:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[0].assignedAt || leftActivities[0].startedAt)}
                        </span>
                        <div className="text-xs text-muted-foreground mt-2">Elapsed:</div>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-xs font-medium font-mono",
                          getElapsedTimeColor(leftActivities[0].assignedAt, leftActivities[0].startedAt)
                        )}>
                          {calculateElapsedTime(leftActivities[0].assignedAt, leftActivities[0].startedAt)}
                        </span>
                      </>
                    )}

                    {leftActivities[0].status === 'skipped' && (
                      <>
                        <div className="text-xs text-muted-foreground">Task Skipped:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[0].completionTime || leftActivities[0].timestamp)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vertical line connecting left column circles */}
          {leftActivities[0] && leftActivities[1] && (
            <div
              className={cn(
                "absolute left-[33px] w-0.5",
                leftActivities[0].status === "completed"
                  ? "bg-success"
                  : "bg-border",
              )}
              style={{ top: "30px", height: "calc(100% - 30px)" }}
            />
          )}

          {/* Row 1 - Right: Pharmacy Clearance */}
          {rightActivities[0] && (
            <div className="relative flex gap-3">
              {/* Horizontal connector from main line */}

              {/* Vertical connector from horizontal line to circle */}

              {/* Status indicator */}
              <div className="relative flex-shrink-0 mt-0.5 z-10">
                {rightActivities[0].status === "completed" ? (
                  <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
                  </div>
                ) : rightActivities[0].status === "skipped" ? (
                  <div className="w-5 h-5 rounded-full bg-muted-foreground flex items-center justify-center">
                    <img src={statusSkippedIcon} alt="Skipped" className="w-3 h-3 invert" />
                  </div>
                ) : rightActivities[0].status === "in-progress" ? (
                  <div className="w-5 h-5 rounded-full border-2 border-success bg-background flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-success" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
                )}
              </div>

              {/* Activity content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-foreground">{rightActivities[0].name}</h4>

                    {rightActivities[0].description && (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {rightActivities[0].description}
                      </p>
                    )}

                    {rightActivities[0].status === "completed" && rightActivities[0].completedBy && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={rightActivities[0].completedBy} label="Completed by" size="sm" />
                      </div>
                    )}

                    {rightActivities[0].status === "in-progress" && rightActivities[0].assignedTo && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={rightActivities[0].assignedTo} label="Assigned to" size="sm" />
                      </div>
                    )}

                    {!rightActivities[0].completedBy &&
                      !rightActivities[0].assignedTo &&
                      (rightActivities[0].staffName || rightActivities[0].doctor) && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                          <span>{rightActivities[0].staffName || rightActivities[0].doctor}</span>
                        </div>
                      )}

                    {rightActivities[0].notes && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-1 rounded">
                        <span className="font-medium">Notes:</span> {rightActivities[0].notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0 gap-1">
                    {rightActivities[0].status === 'completed' && (
                      <>
                        <div className="text-xs text-muted-foreground">Completed:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[0].completionTime || rightActivities[0].timestamp)}
                        </span>
                        {rightActivities[0].timeTakenMinutes !== undefined && (
                          <>
                            <div className="text-xs text-muted-foreground mt-2">Duration:</div>
                            <span className="bg-success/10 text-success px-1.5 py-0.5 rounded text-xs font-medium">
                              {formatDuration(rightActivities[0].timeTakenMinutes)}
                            </span>
                          </>
                        )}
                      </>
                    )}

                    {rightActivities[0].status === 'in-progress' && (
                      <>
                        <div className="text-xs text-muted-foreground">Assigned:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[0].assignedAt || rightActivities[0].startedAt)}
                        </span>
                        <div className="text-xs text-muted-foreground mt-2">Elapsed:</div>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-xs font-medium font-mono",
                          getElapsedTimeColor(rightActivities[0].assignedAt, rightActivities[0].startedAt)
                        )}>
                          {calculateElapsedTime(rightActivities[0].assignedAt, rightActivities[0].startedAt)}
                        </span>
                      </>
                    )}

                    {rightActivities[0].status === 'skipped' && (
                      <>
                        <div className="text-xs text-muted-foreground">Task Skipped:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[0].completionTime || rightActivities[0].timestamp)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vertical line connecting right column circles */}
          {rightActivities[0] && rightActivities[1] && (
            <div
              className={cn(
                "absolute w-0.5 z-0",
                rightActivities[0].status === "completed"
                  ? "bg-success"
                  : "bg-border",
              )}
              style={{ top: "30px", height: "calc(100% - 30px)", left: "calc(50% + 43px)" }}
            />
          )}

          {/* Row 2 - Left: Discharge Summary Finalized */}
          {leftActivities[1] && (
            <div className="relative flex gap-3">
              {/* Horizontal connector from main line */}

              {/* Vertical connector from horizontal line to circle */}

              {/* Status indicator */}
              <div className="relative flex-shrink-0 mt-0.5 z-10">
                {leftActivities[1].status === "completed" ? (
                  <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
                  </div>
                ) : leftActivities[1].status === "skipped" ? (
                  <div className="w-5 h-5 rounded-full bg-muted-foreground flex items-center justify-center">
                    <img src={statusSkippedIcon} alt="Skipped" className="w-3 h-3 invert" />
                  </div>
                ) : leftActivities[1].status === "in-progress" ? (
                  <div className="w-5 h-5 rounded-full border-2 border-success bg-background flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-success" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
                )}
              </div>

              {/* Activity content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-foreground">{leftActivities[1].name}</h4>

                    {leftActivities[1].description && (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {leftActivities[1].description}
                      </p>
                    )}

                    {leftActivities[1].status === "completed" && leftActivities[1].completedBy && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={leftActivities[1].completedBy} label="Completed by" size="sm" />
                      </div>
                    )}

                    {leftActivities[1].status === "in-progress" && leftActivities[1].assignedTo && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={leftActivities[1].assignedTo} label="Assigned to" size="sm" />
                      </div>
                    )}

                    {!leftActivities[1].completedBy &&
                      !leftActivities[1].assignedTo &&
                      (leftActivities[1].staffName || leftActivities[1].doctor) && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                          <span>{leftActivities[1].staffName || leftActivities[1].doctor}</span>
                        </div>
                      )}

                    {leftActivities[1].notes && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-1 rounded">
                        <span className="font-medium">Notes:</span> {leftActivities[1].notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0 gap-1">
                    {leftActivities[1].status === 'completed' && (
                      <>
                        <div className="text-xs text-muted-foreground">Completed:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[1].completionTime || leftActivities[1].timestamp)}
                        </span>
                        {leftActivities[1].timeTakenMinutes !== undefined && (
                          <>
                            <div className="text-xs text-muted-foreground mt-2">Duration:</div>
                            <span className="bg-success/10 text-success px-1.5 py-0.5 rounded text-xs font-medium">
                              {formatDuration(leftActivities[1].timeTakenMinutes)}
                            </span>
                          </>
                        )}
                      </>
                    )}

                    {leftActivities[1].status === 'in-progress' && (
                      <>
                        <div className="text-xs text-muted-foreground">Assigned:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[1].assignedAt || leftActivities[1].startedAt)}
                        </span>
                        <div className="text-xs text-muted-foreground mt-2">Elapsed:</div>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-xs font-medium font-mono",
                          getElapsedTimeColor(leftActivities[1].assignedAt, leftActivities[1].startedAt)
                        )}>
                          {calculateElapsedTime(leftActivities[1].assignedAt, leftActivities[1].startedAt)}
                        </span>
                      </>
                    )}

                    {leftActivities[1].status === 'skipped' && (
                      <>
                        <div className="text-xs text-muted-foreground">Task Skipped:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(leftActivities[1].completionTime || leftActivities[1].timestamp)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Row 2 - Right: Charge Sheet */}
          {rightActivities[1] && (
            <div className="relative flex gap-3">
              {/* Horizontal connector from main line */}

              {/* Vertical connector from horizontal line to circle */}

              {/* Status indicator */}
              <div className="relative flex-shrink-0 mt-0.5 z-10">
                {rightActivities[1].status === "completed" ? (
                  <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
                  </div>
                ) : rightActivities[1].status === "skipped" ? (
                  <div className="w-5 h-5 rounded-full bg-muted-foreground flex items-center justify-center">
                    <img src={statusSkippedIcon} alt="Skipped" className="w-3 h-3 invert" />
                  </div>
                ) : rightActivities[1].status === "in-progress" ? (
                  <div className="w-5 h-5 rounded-full border-2 border-success bg-background flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-success" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
                )}
              </div>

              {/* Activity content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-foreground">{rightActivities[1].name}</h4>

                    {rightActivities[1].description && (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {rightActivities[1].description}
                      </p>
                    )}

                    {rightActivities[1].status === "completed" && rightActivities[1].completedBy && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={rightActivities[1].completedBy} label="Completed by" size="sm" />
                      </div>
                    )}

                    {rightActivities[1].status === "in-progress" && rightActivities[1].assignedTo && (
                      <div className="mt-2">
                        <StaffInfoDisplay staff={rightActivities[1].assignedTo} label="Assigned to" size="sm" />
                      </div>
                    )}

                    {!rightActivities[1].completedBy &&
                      !rightActivities[1].assignedTo &&
                      (rightActivities[1].staffName || rightActivities[1].doctor) && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                          <span>{rightActivities[1].staffName || rightActivities[1].doctor}</span>
                        </div>
                      )}

                    {rightActivities[1].notes && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-1 rounded">
                        <span className="font-medium">Notes:</span> {rightActivities[1].notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end flex-shrink-0 gap-1">
                    {rightActivities[1].status === 'completed' && (
                      <>
                        <div className="text-xs text-muted-foreground">Completed:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[1].completionTime || rightActivities[1].timestamp)}
                        </span>
                        {rightActivities[1].timeTakenMinutes !== undefined && (
                          <>
                            <div className="text-xs text-muted-foreground mt-2">Duration:</div>
                            <span className="bg-success/10 text-success px-1.5 py-0.5 rounded text-xs font-medium">
                              {formatDuration(rightActivities[1].timeTakenMinutes)}
                            </span>
                          </>
                        )}
                      </>
                    )}

                    {rightActivities[1].status === 'in-progress' && (
                      <>
                        <div className="text-xs text-muted-foreground">Assigned:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[1].assignedAt || rightActivities[1].startedAt)}
                        </span>
                        <div className="text-xs text-muted-foreground mt-2">Elapsed:</div>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-xs font-medium font-mono",
                          getElapsedTimeColor(rightActivities[1].assignedAt, rightActivities[1].startedAt)
                        )}>
                          {calculateElapsedTime(rightActivities[1].assignedAt, rightActivities[1].startedAt)}
                        </span>
                      </>
                    )}

                    {rightActivities[1].status === 'skipped' && (
                      <>
                        <div className="text-xs text-muted-foreground">Task Skipped:</div>
                        <span className="text-xs whitespace-nowrap">
                          {formatActivityTimestamp(rightActivities[1].completionTime || rightActivities[1].timestamp)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main connecting line after parallel section */}

      {/* Horizontal extension at the bottom with sharp corner */}
    </div>
  );
};